/**
 * @file BulkAddBills.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-15
 * @description Bulk Add Bills (Cam, 2026-10-12): "multiple file uploads and/or
 *              multi-page document uploads … process each one with a summary
 *              table whereby user can accept or review each bill and PO link
 *              … a progress tracker … continue where they left off".
 *
 *              Upload (BulkBillUpload) → the server splits every file into
 *              bills and reads each one as Add Bill reads an upload → the
 *              summary (BulkBillSummary): Accept saves a Ready bill exactly as
 *              Add Bill's Save would (utils/bulkBillPlan builds the save);
 *              Review queues it for the review step (BulkBillReview: the
 *              normal Add Bill screen, one bill at a time). Possible
 *              duplicates are decided in BulkBillDuplicateDialog. The progress
 *              tracker (BulkBillsProgress) sits across the top throughout.
 *
 *              Progress lives on the server, per user (/api/bulk-bills), so
 *              leaving, reloading or coming back another day carries on: a
 *              batch this tab was working on opens straight back where it
 *              was; one from before asks to carry on or discard it.
 *
 * 1.1.0 (review): Accept asks first (BulkAcceptDialog) — every PO line whose
 *              cost the bills would change, each ticked or not by the user, on
 *              plans worked out afresh — and saves exactly those choices. A
 *              file that could not be stored or split says why on its chip.
 *
 * 1.2.0 (2026-10-15, Cam): Accept asks once, naming the bills dated in a
 * month that should be locked in Xero by now (usePriorMonthConfirm), and
 * sends them confirmed.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import usePersistedState from '../hooks/usePersistedState';
import useBulkBillPlans from '../hooks/useBulkBillPlans';
import { useSlideOutButton } from './SlideOutButton';
import { useEntityModal } from './EntityModalProvider';
import { useAccess } from '../hooks/useAccess';
import Modal from './Modal';
import PdfCanvasPreview from './PdfCanvasPreview';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import LoadingSpinner from './LoadingSpinner';
import BulkBillsProgress from './BulkBillsProgress';
import BulkBillUpload from './BulkBillUpload';
import BulkBillSummary from './BulkBillSummary';
import BulkBillReview from './BulkBillReview';
import BulkBillDuplicateDialog from './BulkBillDuplicateDialog';
import BulkAcceptDialog from './BulkAcceptDialog';
import { reviewQueue, sourceText, acceptPayload } from '../utils/bulkBillPlan';
import usePriorMonthConfirm, { isPriorMonthConfirm, PRIOR_MONTH_CONFIRMED } from '../hooks/usePriorMonthConfirm';
import useXeroLock from '../hooks/useXeroLock';
import { priorMonthQuestionForMany } from '../utils/xeroLockMonth';
import { formatDateTime } from '../utils/dateFormatters';
import './SearchLayout.css';
import './BulkAddBills.css';

const api = async (path, { method = 'GET', body, form } = {}) => {
  const res = await fetch(`${API_URL}/api/bulk-bills${path}`, {
    method,
    credentials: 'include',
    ...(form ? { body: form } : body !== undefined ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {}),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok && data.success !== false, status: res.status, data };
};

function HeaderButtons({ children, deps }) {
  useSlideOutButton(deps);
  return <div className="buttons slideout">{children}</div>;
}

function BulkAddBills({ onNavigate }) {
  const { openEntity } = useEntityModal();
  const { can } = useAccess();
  const mayCreate = can('bills.create');
  const [status, setStatus] = useState(null);
  // "Dated in a month that should be locked" (Cam, 15 Oct 2026): asked once
  // for the bills Accept would save, before any is sent; again per bill only
  // if the server knows a lock this tab does not.
  const priorMonth = usePriorMonthConfirm();
  const xeroLock = useXeroLock();
  // The batch this tab is working on, and where in it — kept across
  // navigating away (the nav store); the batch itself is the server's.
  const [batchId, setBatchId] = usePersistedState('bulkAddBills.batchId', null);
  const [view, setView] = usePersistedState('bulkAddBills.view', 'summary');
  const [reviewItemId, setReviewItemId] = usePersistedState('bulkAddBills.reviewItemId', null);
  const [data, setData] = useState(null);
  const [resumeOffer, setResumeOffer] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [busyText, setBusyText] = useState('');
  const [preview, setPreview] = useState(null);
  const [dupItem, setDupItem] = useState(null);
  const [pushSettings, setPushSettings] = useState({});
  const [alert, setAlert] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [showMore, setShowMore] = useState(false);
  // Accept's question: { entries: [{ item, plan }], dropped: [...] }.
  const [acceptAsk, setAcceptAsk] = useState(null);

  const items = useMemo(() => data?.items || [], [data]);
  const { plans, reloadPos, freshPlans, firstAllowedDate } = useBulkBillPlans(items);

  // ── Loading ──
  const load = useCallback(async (id, { refresh = false } = {}) => {
    if (!id) return null;
    const r = await api(`/batches/${id}${refresh ? '?refresh=1' : ''}`);
    if (!r.ok) {
      if (r.status === 404) { setBatchId(null); setData(null); }
      return null;
    }
    setData(r.data);
    return r.data;
  }, [setBatchId]);

  useEffect(() => {
    let live = true;
    (async () => {
      const r = await api('/status');
      if (!live) return;
      setStatus(r.data);
      fetch(`${API_URL}/api/bills/push-settings`, { credentials: 'include' })
        .then(x => x.json()).then(d => { if (live && d.success) setPushSettings(d); }).catch(() => {});
      if (!r.data?.ready) return;
      const open = r.data.open_batch;
      if (open && Number(open.id) === Number(batchId)) {
        await load(open.id, { refresh: true });
      } else if (open) {
        setResumeOffer(open);
      } else if (batchId) {
        // A finished batch this tab was showing: show how it ended.
        const d = await load(batchId);
        if (!d || d.batch.status === 'discarded') { setBatchId(null); setData(null); }
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While files are split and bills read, look again every few seconds.
  const working = !!data && data.batch?.status === 'open' && ((data.batch.splitting || 0) > 0 || (data.progress?.reading || 0) > 0);
  useEffect(() => {
    if (!working || !batchId) return undefined;
    const t = setTimeout(() => load(batchId), 3000);
    return () => clearTimeout(t);
  }, [working, batchId, data, load]);

  const refresh = useCallback(async () => { await load(batchId, { refresh: true }); reloadPos(); }, [batchId, load, reloadPos]);

  // ── Upload ──
  const upload = async (files) => {
    setUploading(true);
    try {
      const form = new FormData();
      files.forEach(f => form.append('files', f));
      const adding = data?.batch?.status === 'open' && batchId;
      const r = await api(adding ? `/batches/${batchId}/files` : '/batches', { method: 'POST', form });
      if (!r.ok) {
        if (r.data?.code === 'OPEN_BATCH') {
          setResumeOffer({ id: r.data.batch_id });
          setAlert({ type: 'warning', message: r.data.error });
          return;
        }
        throw new Error(r.data?.error || 'The files could not be uploaded');
      }
      setBatchId(r.data.batch_id);
      setView('summary');
      setShowMore(false);
      await load(r.data.batch_id);
    } catch (e) {
      setAlert({ type: 'error', message: e.message });
    } finally {
      setUploading(false);
    }
  };

  // ── Resume / discard ──
  const resume = async (id) => {
    setResumeOffer(null);
    setBatchId(id);
    setView('summary');
    await load(id, { refresh: true });
  };
  const discard = (id) => setConfirm({
    title: 'Discard this batch?',
    message: 'The bills not yet saved in it are thrown away. Bills already saved from it stay as they are.',
    confirmText: 'Discard batch',
    onConfirm: async () => {
      setConfirm(null);
      const r = await api(`/batches/${id}/discard`, { method: 'POST' });
      if (!r.ok) { setAlert({ type: 'error', message: r.data?.error || 'The batch could not be discarded' }); return; }
      setResumeOffer(null);
      setBatchId(null);
      setData(null);
      setView('summary');
      setSelected(new Set());
    },
  });

  // ── Row actions ──
  const act = async (path, failure, { method = 'POST', body = {} } = {}) => {
    const r = await api(path, { method, body });
    if (!r.ok) setAlert({ type: 'error', message: r.data?.error || failure });
    await load(batchId);
    return r.ok;
  };
  const setChoice = (id, value) => act(`/items/${id}`, 'That choice could not be saved', { method: 'PATCH', body: value });

  const onReview = async (ids, on = true) => {
    setBusy(true);
    for (const id of ids) {
      // eslint-disable-next-line no-await-in-loop
      await api(`/items/${id}`, { method: 'PATCH', body: { choice: on ? 'review' : null } });
    }
    setBusy(false);
    await load(batchId);
  };

  /**
   * Accept: Add Bill's save, one bill at a time, each with the PO cost
   * choices the user ticked for it in BulkAcceptDialog.
   */
  const onAccept = async (entries, ticksByItem) => {
    const ready = entries.filter(e => acceptPayload(e.plan, ticksByItem.get(e.item.id))).map(e => e.item);
    const payloadOf = new Map(entries.map(e => [e.item.id, acceptPayload(e.plan, ticksByItem.get(e.item.id))]));
    setAcceptAsk(null);
    if (!ready.length) return;
    // One question for the batch, naming the bills dated in a month that
    // should be locked in Xero by now (the server asks each save otherwise).
    let priorMonthConfirmed = false;
    if (xeroLock.loaded) {
      const question = priorMonthQuestionForMany(ready.map((it) => {
        const p = payloadOf.get(it.id) || {};
        const credit = String(p.transaction_type || 'BILL').toUpperCase() === 'CREDIT';
        const ref = String(p.supplier_reference || '').trim();
        return { what: ref ? `${credit ? 'Supplier credit' : 'Bill'} ${ref}` : `${it.seq}. ${sourceText(it)}`, dates: [p.bill_date] };
      }), xeroLock);
      if (question) {
        if (!(await priorMonth.ask(question))) return;
        priorMonthConfirmed = true;
      }
    }
    setBusy(true);
    const problems = [];
    const notes = [];
    for (let n = 0; n < ready.length; n++) {
      const it = ready[n];
      setBusyId(it.id);
      setBusyText(ready.length > 1 ? `Saving ${n + 1} of ${ready.length}…` : 'Saving…');
      try {
        const send = (extra) => fetch(`${API_URL}/api/bills`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payloadOf.get(it.id), ...(priorMonthConfirmed ? PRIOR_MONTH_CONFIRMED : {}), ...extra }),
        });
        // eslint-disable-next-line no-await-in-loop
        let res = await send({});
        // eslint-disable-next-line no-await-in-loop
        let d = await res.json().catch(() => ({}));
        // Asked by the server although this tab did not know (its lock moved):
        // asked for this bill; Go back leaves it unsaved, to review.
        if (res.status === 409 && isPriorMonthConfirm(d)) {
          // eslint-disable-next-line no-await-in-loop
          if (await priorMonth.ask(d)) {
            // eslint-disable-next-line no-await-in-loop
            res = await send(PRIOR_MONTH_CONFIRMED);
            // eslint-disable-next-line no-await-in-loop
            d = await res.json().catch(() => ({}));
          } else {
            problems.push(`${it.seq}. ${it.parsed?.suggested?.supplier_reference || sourceText(it)}: not saved — dated in a month that should be locked in Xero.`);
            continue;
          }
        }
        if (!res.ok || !d.success) {
          problems.push(`${it.seq}. ${it.parsed?.suggested?.supplier_reference || sourceText(it)}: ${d.error || 'not saved'} — review it.`);
        } else {
          if (d.attachment && !d.attachment.ok) notes.push(`${it.seq}. saved, but its PDF could not be attached — "Attach again" on its row.`);
          if (d.quantity_differences?.length) notes.push(`${it.seq}. saved; quantities differ from the PO — check them with the supplier.`);
        }
      } catch (e) {
        problems.push(`${it.seq}. ${e.message}`);
      }
    }
    setBusyId(null);
    setBusyText('');
    setBusy(false);
    setSelected(new Set());
    await refresh();
    if (problems.length || notes.length) {
      setAlert({
        type: problems.length ? 'warning' : 'info',
        message: [
          problems.length ? `Not saved (each now needs review):\n${problems.join('\n')}` : '',
          notes.join('\n'),
        ].filter(Boolean).join('\n\n'),
      });
    }
  };

  const onSkip = (item) => act(`/items/${item.id}/skip`, 'It could not be skipped');
  const onDiscard = (item) => setConfirm({
    title: 'Discard these pages?',
    message: `${sourceText(item)} — nothing is saved from them. Undo brings them back.`,
    confirmText: 'Discard',
    onConfirm: async () => { setConfirm(null); await act(`/items/${item.id}/discard`, 'They could not be discarded'); },
  });
  const onReopen = (item) => act(`/items/${item.id}/reopen`, 'It could not be brought back');
  const onAttach = (item) => act(`/items/${item.id}/attach`, 'The PDF could not be attached');
  const onRetryRead = (item) => act(`/items/${item.id}/retry-read`, 'It could not be read again');

  const chooseDuplicate = async (item, value) => {
    setDupItem(null);
    const ok = await setChoice(item.id, value);
    // Replace goes straight to the review list, to be done in Edit Bill.
    if (ok && value.dup_choice === 'replace') await api(`/items/${item.id}`, { method: 'PATCH', body: { choice: 'review' } }).then(() => load(batchId));
  };

  // ── Review step ──
  const queue = useMemo(() => reviewQueue(items), [items]);
  const current = queue.find(i => i.id === reviewItemId) || queue[0] || null;
  const position = current ? queue.indexOf(current) + 1 : 0;
  const startReview = () => { setReviewItemId(queue[0]?.id || null); setView('review'); };
  const afterReviewed = async () => {
    const next = queue[position] || null; // the one after this
    const d = await load(batchId, { refresh: true });
    reloadPos();
    const q = reviewQueue(d?.items || []);
    const n = q.find(i => i.id === next?.id) || q[0] || null;
    if (n) setReviewItemId(n.id);
    else { setReviewItemId(null); setView('summary'); }
  };
  const skipCurrent = async () => {
    if (!current) return;
    const isBill = current.kind === 'bill' || current.kind === 'credit';
    const r = await api(`/items/${current.id}/${isBill ? 'skip' : 'discard'}`, { method: 'POST', body: {} });
    if (!r.ok) { setAlert({ type: 'error', message: r.data?.error || 'It could not be skipped' }); return; }
    await afterReviewed();
  };
  const pick = (step) => {
    const n = queue[position - 1 + step];
    if (n) setReviewItemId(n.id);
  };

  // ── Selection ──
  const toggle = (id) => setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = (on) => setSelected(on ? new Set(items.filter(i => !i.outcome).map(i => i.id)) : new Set());
  const selectedReady = [...selected].filter(id => plans.get(id)?.status === 'ready');
  const allReady = items.filter(i => plans.get(i.id)?.status === 'ready').map(i => i.id);
  const selectedOpen = [...selected].filter(id => { const i = items.find(x => x.id === id); return i && !i.outcome; });

  /**
   * Before Accept: the batch and its POs read afresh, each bill's plan worked
   * out again, and the PO cost question asked line by line (BulkAcceptDialog).
   */
  const confirmAccept = async (ids) => {
    if (!ids.length) return;
    setBusy(true);
    setBusyText('Checking against the POs as they are now…');
    try {
      const d = await load(batchId, { refresh: true });
      const list = d?.items || items;
      const fresh = await freshPlans(list);
      const chosen = ids.map(id => list.find(i => i.id === id)).filter(i => i && !i.outcome);
      const entries = chosen.filter(i => fresh.get(i.id)?.status === 'ready').map(i => ({ item: i, plan: fresh.get(i.id) }));
      const dropped = chosen.filter(i => fresh.get(i.id)?.status !== 'ready').map(i => ({ item: i, plan: fresh.get(i.id) || { reasons: [] } }));
      if (entries.length || dropped.length) setAcceptAsk({ entries, dropped });
    } catch (e) {
      setAlert({ type: 'error', message: `The bills could not be checked: ${e.message}` });
    } finally {
      setBusy(false);
      setBusyText('');
    }
  };

  const batchOpen = data?.batch?.status === 'open';
  const statusLabel = data?.batch?.status === 'finished' ? 'Finished' : data?.batch?.status === 'discarded' ? 'Discarded' : null;

  // ── Render ──
  if (!status) return <div className="bulk-add-bills"><LoadingSpinner /></div>;

  const header = (
    <div className="search-header bulk-bills-header">
      <div className="search-header-inner">
        <span className="page-subtitle">
          Upload many supplier invoices at once — each bill is found, read and checked against its PO, then accepted or reviewed.
        </span>
      </div>
      <div className="search-header-inner">
        <HeaderButtons deps={[!!data, batchOpen, view, allReady.length, queue.length, busy]}>
          <div>
            <button className="btn-slideout" onClick={() => onNavigate && onNavigate('search-bills')}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">arrow_back</span></div>
              <span className="btn-label">Bills</span>
            </button>
          </div>
          {data && (
            <>
              <div>
                <button className="btn-slideout" onClick={() => setShowMore(v => !v)} disabled={!batchOpen || uploading || !mayCreate} data-write-action
                  title={!batchOpen ? 'This batch is finished — start a new one' : 'Add more PDFs to this batch'}>
                  <div className="icon-wrapper"><span className="material-symbols-rounded">note_add</span></div>
                  <span className="btn-label">Add more files</span>
                </button>
              </div>
              <div>
                <button className="btn-slideout" onClick={() => discard(batchId)} disabled={!batchOpen || busy} data-write-action
                  title={!batchOpen ? 'This batch is finished' : 'Throw away what is not saved yet'}>
                  <div className="icon-wrapper"><span className="material-symbols-rounded">delete_sweep</span></div>
                  <span className="btn-label">Discard batch</span>
                </button>
              </div>
              <div>
                <button className="btn-slideout" onClick={() => confirmAccept(allReady)} disabled={busy || !allReady.length || view !== 'summary'} data-write-action
                  title={allReady.length ? `Save the ${allReady.length} Ready bill${allReady.length === 1 ? '' : 's'}` : 'No bill is Ready to accept'}>
                  <div className="icon-wrapper"><span className="material-symbols-rounded">done_all</span></div>
                  <span className="btn-label">Accept all ready ({allReady.length})</span>
                </button>
              </div>
              <div>
                <button className="btn-slideout btn-slideout-primary" onClick={startReview} disabled={busy || !queue.length || view === 'review'}
                  title={queue.length ? 'Work through the bills chosen for review, one at a time' : 'Choose Review on a bill first'}>
                  <div className="icon-wrapper"><span className="material-symbols-rounded">rate_review</span></div>
                  <span className="btn-label">Start review ({queue.length})</span>
                </button>
              </div>
            </>
          )}
          {data && !batchOpen && (
            <div>
              <button className="btn-slideout" onClick={() => { setBatchId(null); setData(null); setView('summary'); }}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">add</span></div>
                <span className="btn-label">New batch</span>
              </button>
            </div>
          )}
        </HeaderButtons>
      </div>
    </div>
  );

  return (
    <div className="bulk-add-bills search-container">
      {header}

      {!status.ready && (
        <div className="bulk-bills-callout error">
          <span className="material-symbols-rounded">construction</span>
          <div>{status.message || 'Bulk Add Bills is not ready yet.'}</div>
        </div>
      )}

      {status.ready && resumeOffer && !data && (
        <div className="bulk-bills-callout info bulk-bills-resume">
          <span className="material-symbols-rounded">history</span>
          <div>
            <strong>You have a Bulk Add Bills batch that is not finished</strong>
            {resumeOffer.created_at ? ` — started ${formatDateTime(resumeOffer.created_at)}` : ''}
            {resumeOffer.items != null ? `, ${resumeOffer.done} of ${resumeOffer.items} bills done` : ''}.
            Carry on where you left off, or discard it.
          </div>
          <HeaderButtons deps={[resumeOffer.id]}>
            <div><button className="btn-slideout" onClick={() => discard(resumeOffer.id)} data-write-action>
              <div className="icon-wrapper"><span className="material-symbols-rounded">delete_sweep</span></div>
              <span className="btn-label">Discard it</span>
            </button></div>
            <div><button className="btn-slideout btn-slideout-primary" onClick={() => resume(resumeOffer.id)}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">play_arrow</span></div>
              <span className="btn-label">Carry on</span>
            </button></div>
          </HeaderButtons>
        </div>
      )}

      {data && (
        <BulkBillsProgress
          progress={data.progress}
          extra={[
            data.batch.splitting ? `splitting ${data.batch.splitting} file${data.batch.splitting === 1 ? '' : 's'}…` : '',
            busyText,
            statusLabel ? `Batch ${statusLabel.toLowerCase()}` : '',
          ].filter(Boolean).join(' · ')}
        />
      )}

      {status.ready && !data && !resumeOffer && (
        mayCreate
          ? <BulkBillUpload onUpload={upload} busy={uploading} maxFiles={status.max_files || 50} />
          : <div className="bulk-bills-callout warn"><span className="material-symbols-rounded">lock</span><div>Adding bills needs the "Create bills" permission.</div></div>
      )}

      {data && showMore && batchOpen && (
        <BulkBillUpload onUpload={upload} busy={uploading} maxFiles={status.max_files || 50} compact />
      )}

      {data && view === 'summary' && (
        <>
          <div className="bulk-bills-files muted">
            {(data.batch.source_files || []).map(f => (
              <span key={f.index} className={`bulk-file-chip${f.error ? ' error' : ''}`} title={f.note || f.error || ''}>
                <span className="material-symbols-rounded">{f.error ? 'error' : f.split ? 'picture_as_pdf' : 'hourglass_top'}</span>
                {f.name}{f.pages ? ` · ${f.pages} page${f.pages === 1 ? '' : 's'}` : ''}
                {!f.split && !f.stored ? ' · uploading…' : ''}
                {!f.split && f.stored ? ' · splitting…' : ''}
                {f.method === 'text' && f.pages > 1 ? ' · split on its printed text' : ''}
                {f.error ? ` · ${f.split ? '' : 'trying again: '}${f.error}` : ''}
              </span>
            ))}
          </div>
          {selected.size > 0 && (
            <div className="bulk-bills-selbar">
              <span>{selected.size} selected</span>
              <HeaderButtons deps={[selected.size, selectedReady.length, busy]}>
                <div><button className="btn-slideout" onClick={() => confirmAccept(selectedReady)} disabled={busy || !selectedReady.length} data-write-action
                  title={selectedReady.length ? `Save the ${selectedReady.length} Ready of them` : 'None of them is Ready'}>
                  <div className="icon-wrapper"><span className="material-symbols-rounded">check_circle</span></div>
                  <span className="btn-label">Accept ready ({selectedReady.length})</span>
                </button></div>
                <div><button className="btn-slideout" onClick={() => onReview(selectedOpen, true)} disabled={busy || !selectedOpen.length} data-write-action>
                  <div className="icon-wrapper"><span className="material-symbols-rounded">rate_review</span></div>
                  <span className="btn-label">Review them ({selectedOpen.length})</span>
                </button></div>
                <div><button className="btn-slideout" onClick={() => setSelected(new Set())}>
                  <div className="icon-wrapper"><span className="material-symbols-rounded">deselect</span></div>
                  <span className="btn-label">Clear</span>
                </button></div>
              </HeaderButtons>
            </div>
          )}
          <BulkBillSummary
            items={items}
            plans={plans}
            selected={selected}
            onToggle={toggle}
            onToggleAll={toggleAll}
            busy={busy}
            busyId={busyId}
            onPreview={setPreview}
            onAccept={(ids) => confirmAccept(ids)}
            onReview={onReview}
            onDuplicate={setDupItem}
            onSkip={onSkip}
            onDiscard={onDiscard}
            onReopen={onReopen}
            onAttach={onAttach}
            onRetryRead={onRetryRead}
            onOpenBill={(id) => openEntity('bill', id)}
          />
        </>
      )}

      {data && view === 'review' && (
        current ? (
          <BulkBillReview
            queue={queue}
            current={current}
            plan={plans.get(current.id)}
            position={position}
            busy={busy}
            onSaved={afterReviewed}
            onSkip={skipCurrent}
            onBack={() => { setView('summary'); refresh(); }}
            onPick={pick}
          />
        ) : (
          <div className="bulk-bills-callout info">
            <span className="material-symbols-rounded">task_alt</span>
            <div>Nothing left on the review list. <button className="bulk-link" onClick={() => setView('summary')}>Back to the summary</button></div>
          </div>
        )
      )}

      {preview && (
        <Modal isOpen onClose={() => setPreview(null)} size="xl" className="bulk-bills-preview" title={`Pages — ${sourceText(preview)}`}>
          <PdfCanvasPreview
            loadKey={preview.id}
            load={async () => {
              const res = await fetch(`${API_URL}/api/bulk-bills/items/${preview.id}/pdf`, { method: 'POST', credentials: 'include' });
              if (!res.ok) throw new Error('The pages could not be loaded');
              return res.arrayBuffer();
            }}
          />
        </Modal>
      )}

      {acceptAsk && (
        <BulkAcceptDialog
          entries={acceptAsk.entries}
          dropped={acceptAsk.dropped}
          busy={busy}
          onCancel={() => setAcceptAsk(null)}
          onConfirm={(ticks) => onAccept(acceptAsk.entries, ticks)}
        />
      )}

      {dupItem && (
        <BulkBillDuplicateDialog
          item={dupItem}
          plan={plans.get(dupItem.id)}
          batchOriginal={dupItem.batch_duplicate_of ? items.find(i => i.id === dupItem.batch_duplicate_of) : null}
          pushSettings={pushSettings}
          firstAllowedDate={firstAllowedDate}
          onChoose={(value) => chooseDuplicate(dupItem, value)}
          onClose={() => setDupItem(null)}
        />
      )}

      {priorMonth.dialog}
      <ConfirmDialog
        isOpen={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmText={confirm?.confirmText}
        type={confirm?.type || 'warning'}
        onConfirm={() => confirm?.onConfirm?.()}
        onCancel={() => setConfirm(null)}
      />
      <AlertDialog
        isOpen={!!alert}
        type={alert?.type}
        message={alert?.message}
        onClose={() => setAlert(null)}
      />
    </div>
  );
}

export default BulkAddBills;
